import React from 'react'
import axios from 'axios'
import {
  BsChevronLeft,
  BsChevronRight,
  BsSearch,
  BsX,
} from 'react-icons/bs'
import {
  MdAutoAwesome,
  MdGroups,
  MdHourglassTop,
  MdPersonOutline,
  MdSchool,
  MdTaskAlt,
} from 'react-icons/md'
import { getRequestHeaders } from '../api'
import { baseUrl } from '../assets/Animations'
import StudentProfile from './StudentProfile'
import TeacherProfile from './TeacherProfile'
import TeacherDirectory from './TeacherDirectory'
import TodayTasks from './TodayTasks'
import { askAtharoAi } from './atharoAiApi'
import { getTeacherImageUrl } from './teacherProfileUtils'
import { getTasksDueToday } from './taskUtils'
import atharoLogo from '../img/new-atharo-logo.png'
import './atharo-hub.css'

const pageButtons = [
  { label: 'Tasks', icon: MdTaskAlt },
  { label: 'Teacher Database', icon: MdSchool },
  { label: 'Teacher Onboarding', icon: MdGroups },
  { label: 'Student Waitlist', icon: MdHourglassTop },
]
const EMPTY_TASKS = []

const normalizeText = (value) => String(value || '').trim()
const normalizeForComparison = (value) => normalizeText(value).toLowerCase()
const formatSourceDate = (value) => {
  const normalized = normalizeText(value)
  if (!normalized) return ''

  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return normalized

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}
const teacherIdFrom = (teacher) => normalizeText(
  teacher?.TeacherID || teacher?.teacherId || teacher?.teacherID || teacher?.id
)
const isOldTeacher = (teacher) => normalizeForComparison(
  teacher?.Status || teacher?.status || teacher?.teacherStatus
).replace(/[-_]+/g, ' ') === 'left school'
const familyIdFrom = (family) => normalizeText(
  family?.familyId || family?.familyID || family?._id || family?.id
)

const getTeacherInstruments = (teacher) => {
  const instruments = Array.isArray(teacher?.InstrumentList)
    ? teacher.InstrumentList
    : Array.isArray(teacher?.instruments)
      ? teacher.instruments
      : []

  return [...new Set(instruments
    .map((instrument) => normalizeText(
      typeof instrument === 'string'
        ? instrument
        : instrument?.name || instrument?.instrument || instrument?.parentInstrument
    ))
    .filter(Boolean))]
}

const getParentName = (family) => {
  const parentName = normalizeText(family?.primaryParentName)
  const familyName = normalizeText(family?.familyName)
  if (!parentName) return familyName
  if (!familyName || normalizeForComparison(parentName).includes(normalizeForComparison(familyName))) {
    return parentName
  }
  return `${parentName} ${familyName}`
}

const isVisibleToUser = (record, userData) => {
  const isManager = userData?.PermissionLevel === 'manager' && userData?.IsAdmin
  if (!isManager) return true

  const userSchool = normalizeForComparison(userData?.School)
  const recordSchool = normalizeForComparison(record?.School || record?.school)
  return !recordSchool || recordSchool === userSchool
}

const normalizeTeacherResults = (payload, userData) => {
  const teachers = Array.isArray(payload) ? payload : []
  const seen = new Set()

  return teachers.reduce((results, teacher) => {
    if (!teacher || !isVisibleToUser(teacher, userData)) return results

    const name = normalizeText(teacher.Name || teacher.name)
    if (!name) return results

    const id = normalizeText(teacherIdFrom(teacher) || teacher._id || name)
    const key = `teacher-${id}`
    if (seen.has(key)) return results
    seen.add(key)

    results.push({
      id: key,
      name,
      detail: getTeacherInstruments(teacher).join(', '),
      image: getTeacherImageUrl(teacher),
      type: 'Teacher',
      searchTypeLabel: isOldTeacher(teacher) ? 'Old Teacher' : 'Teacher',
      teacher,
    })
    return results
  }, [])
}

const normalizeFamilyResults = (payload, userData) => {
  const families = Array.isArray(payload) ? payload : []
  const seen = new Set()

  return families.reduce((results, family) => {
    if (!family || !isVisibleToUser(family, userData)) return results

    const students = Array.isArray(family.students) ? family.students : []
    const studentNames = students
      .map((student) => normalizeText(student?.studentName || student?.name))
      .filter(Boolean)
    const familyName = normalizeText(family.familyName)
    const directStudentName = normalizeText(family.studentName || family.name)
    const name = familyName || directStudentName
    if (!name) return results

    const parentName = getParentName(family)
    const detailParts = studentNames.length > 0
      ? studentNames
      : directStudentName && directStudentName !== name
        ? [directStudentName]
        : []
    const detail = `${detailParts.join(', ')}${parentName && parentName !== name ? ` (${parentName})` : ''}`
    const id = normalizeText(family.familyId || family.familyID || family._id || family.studentId || name)
    const key = `student-${id}`
    if (seen.has(key)) return results
    seen.add(key)

    results.push({
      id: key,
      name,
      detail,
      image: '',
      type: 'Student',
      family,
    })
    return results
  }, [])
}

const PersonAvatar = ({ person }) => {
  const [imageFailed, setImageFailed] = React.useState(false)

  if (person.image && !imageFailed) {
    return (
      <span className='atharo-hub__person-avatar'>
        <img src={person.image} alt='' onError={() => setImageFailed(true)} />
      </span>
    )
  }

  return (
    <span className='atharo-hub__person-avatar atharo-hub__person-avatar--placeholder' aria-hidden='true'>
      <MdPersonOutline />
    </span>
  )
}

const AtharoAi = ({ messages, draft, setDraft, onSubmit, isLoading }) => {
  const conversationEndRef = React.useRef(null)

  React.useLayoutEffect(() => {
    const conversationEnd = conversationEndRef.current
    if (typeof conversationEnd?.scrollIntoView === 'function') {
      conversationEnd.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [messages, isLoading])

  return (
    <div className='atharo-hub__ai-screen'>
      <div className='atharo-hub__ai-messages' aria-live='polite'>
      {messages.map((message) => (
        <div className='atharo-hub__ai-exchange' key={message.id}>
          <div className={`atharo-hub__ai-message atharo-hub__ai-message--${message.role}`}>
            {message.text}
          </div>
          {message.role === 'answer' && Array.isArray(message.sources) && message.sources.map((source, index) => (
            <a
              className='atharo-hub__ai-source'
              href={source.url || undefined}
              target={source.url ? '_blank' : undefined}
              rel={source.url ? 'noreferrer' : undefined}
              key={`${message.id}-source-${source.url || source.title || index}`}
            >
              <span className='atharo-hub__ai-source-content'>
                <strong>{source.title || 'Confluence source'}</strong>
                {source.updatedAt && (
                  <span className='atharo-hub__ai-source-date'>
                    Updated {formatSourceDate(source.updatedAt)}
                  </span>
                )}
                {source.excerpt && <span className='atharo-hub__ai-source-excerpt'>{source.excerpt}</span>}
                {source.url && <span className='atharo-hub__ai-source-link'>View in Confluence</span>}
              </span>
              <img className='atharo-hub__ai-source-logo' src={atharoLogo} alt='' aria-hidden='true' />
            </a>
          ))}
        </div>
      ))}
        {isLoading && (
          <div className='atharo-hub__ai-status' role='status'>
            <span className='atharo-hub__ai-status-dot' />
            Searching Confluence…
          </div>
        )}
        <div ref={conversationEndRef} aria-hidden='true' />
      </div>
      <form className='atharo-hub__ai-composer' onSubmit={onSubmit}>
        <input
          type='text'
          placeholder='Ask a follow-up…'
          aria-label='Ask a follow-up to Atharo AI'
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          autoComplete='off'
          disabled={isLoading}
        />
        <button type='submit' aria-label='Submit Atharo AI question' disabled={isLoading || !draft.trim()}>
          <BsChevronRight aria-hidden='true' />
        </button>
      </form>
    </div>
  )
}

const AtharoHub = ({
  userData,
  onViewFullTeacherProfile,
  onViewFullStudentProfile,
  onOpenTask,
  todos = EMPTY_TASKS,
  setTodos,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [searchResults, setSearchResults] = React.useState([])
  const [isSearching, setIsSearching] = React.useState(false)
  const [searchError, setSearchError] = React.useState('')
  const [selectedTeacher, setSelectedTeacher] = React.useState(null)
  const [selectedFamily, setSelectedFamily] = React.useState(null)
  const [selectedTeacherTab, setSelectedTeacherTab] = React.useState('contact')
  const [recentProfiles, setRecentProfiles] = React.useState([])
  const [isTeacherDirectoryOpen, setIsTeacherDirectoryOpen] = React.useState(false)
  const [isTasksOpen, setIsTasksOpen] = React.useState(false)
  const [hubTasks, setHubTasks] = React.useState(Array.isArray(todos) ? todos : [])
  const [tasksLoading, setTasksLoading] = React.useState(false)
  const [tasksError, setTasksError] = React.useState('')
  const [isAiOpen, setIsAiOpen] = React.useState(false)
  const [aiDraft, setAiDraft] = React.useState('')
  const [aiMessages, setAiMessages] = React.useState([])
  const [aiConversationId, setAiConversationId] = React.useState('')
  const [isAiLoading, setIsAiLoading] = React.useState(false)
  const toggleButtonRef = React.useRef(null)
  const requestSequence = React.useRef(0)
  const teacherProfileRequests = React.useRef(new Set())
  const teacherSearchProfileCache = React.useRef(new Map())
  const aiMessageSequence = React.useRef(0)
  const hasRequestedTasks = React.useRef(false)

  const closeHub = React.useCallback(() => {
    setIsOpen(false)
    window.requestAnimationFrame(() => toggleButtonRef.current?.focus())
  }, [])

  React.useEffect(() => {
    if (!isOpen) return undefined

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeHub()
    }

    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [closeHub, isOpen])

  React.useEffect(() => {
    if (Array.isArray(todos)) setHubTasks(todos)
  }, [todos])

  const loadTasks = React.useCallback(async () => {
    hasRequestedTasks.current = true
    setTasksLoading(true)
    setTasksError('')

    try {
      const response = await axios.get(`${baseUrl}/tasks`, getRequestHeaders())
      const nextTasks = Array.isArray(response.data) ? response.data : []
      setHubTasks(nextTasks)
      if (typeof setTodos === 'function') setTodos(nextTasks)
    } catch (requestError) {
      const responseMessage = (
        requestError?.response?.data?.message
        || requestError?.response?.data
        || 'Please try again.'
      )
      setTasksError(typeof responseMessage === 'string' ? responseMessage : 'Please try again.')
    } finally {
      setTasksLoading(false)
    }
  }, [setTodos])

  React.useEffect(() => {
    const tasksAreConnected = typeof setTodos === 'function' || typeof onOpenTask === 'function'
    if (tasksAreConnected && !hasRequestedTasks.current) loadTasks()
  }, [loadTasks, onOpenTask, setTodos])

  React.useEffect(() => {
    const query = searchQuery.trim()
    const currentSequence = requestSequence.current + 1
    requestSequence.current = currentSequence

    if (query.length < 2) {
      setSearchResults([])
      setIsSearching(false)
      setSearchError('')
      return undefined
    }

    let isCurrent = true
    setIsSearching(true)
    setSearchError('')

    const timeout = window.setTimeout(async () => {
      const encodedQuery = encodeURIComponent(query)
      const requests = await Promise.allSettled([
        axios.get(`${baseUrl}/teacher-search/${encodedQuery}`, getRequestHeaders()),
        axios.get(`${baseUrl}/search/${encodedQuery}`, getRequestHeaders()),
      ])

      if (!isCurrent || requestSequence.current !== currentSequence) return

      const [teacherRequest, familyRequest] = requests
      let teachers = teacherRequest.status === 'fulfilled'
        ? normalizeTeacherResults(teacherRequest.value.data, userData)
        : []
      const families = familyRequest.status === 'fulfilled'
        ? normalizeFamilyResults(familyRequest.value.data, userData)
        : []

      const teachersMissingStatus = teachers.filter(({ teacher }) => !normalizeText(
        teacher?.Status || teacher?.status || teacher?.teacherStatus
      ))
      if (teachersMissingStatus.length > 0) {
        const enrichedTeachers = await Promise.all(teachers.map(async (person) => {
          if (normalizeText(
            person.teacher?.Status || person.teacher?.status || person.teacher?.teacherStatus
          )) return person

          const identifier = normalizeText(
            person.teacher?.Email
            || person.teacher?.email
            || teacherIdFrom(person.teacher)
          )
          if (!identifier) return person

          const cacheKey = normalizeForComparison(identifier)
          let profileRequest = teacherSearchProfileCache.current.get(cacheKey)
          if (!profileRequest) {
            profileRequest = axios.get(
              `${baseUrl}/data/${encodeURIComponent(identifier)}`,
              getRequestHeaders()
            ).then((response) => {
              const loadedTeacher = response?.data?.teacher || response?.data
              return loadedTeacher && typeof loadedTeacher === 'object' && !Array.isArray(loadedTeacher)
                ? loadedTeacher
                : null
            }).catch(() => {
              teacherSearchProfileCache.current.delete(cacheKey)
              return null
            })
            teacherSearchProfileCache.current.set(cacheKey, profileRequest)
          }

          const loadedTeacher = await profileRequest
          if (!loadedTeacher) return person
          const teacher = { ...person.teacher, ...loadedTeacher }
          return {
            ...person,
            searchTypeLabel: isOldTeacher(teacher) ? 'Old Teacher' : 'Teacher',
            teacher,
          }
        }))
        teachers = enrichedTeachers
      }

      if (!isCurrent || requestSequence.current !== currentSequence) return

      setSearchResults([...teachers, ...families])
      setSearchError(
        teacherRequest.status === 'rejected' && familyRequest.status === 'rejected'
          ? 'People search is unavailable right now. Please try again.'
          : ''
      )
      setIsSearching(false)
    }, 250)

    return () => {
      isCurrent = false
      window.clearTimeout(timeout)
    }
  }, [searchQuery, userData])

  const hasSearchQuery = searchQuery.trim().length >= 2
  const tasksDueToday = React.useMemo(() => getTasksDueToday(hubTasks), [hubTasks])
  const visiblePageButtons = hasSearchQuery
    ? pageButtons.filter(({ label }) => label === 'Teacher Onboarding' || label === 'Student Waitlist')
    : pageButtons

  const selectTeacher = React.useCallback((person) => {
    if (!person?.teacher) return
    setRecentProfiles((current) => [
      person,
      ...current.filter((recent) => recent.id !== person.id),
    ].slice(0, 5))
    setSelectedFamily(null)
    setIsTasksOpen(false)
    setIsTeacherDirectoryOpen(false)
    setIsAiOpen(false)
    setSelectedTeacher(person.teacher)
    setSelectedTeacherTab('contact')
    setSearchQuery('')
    setSearchResults([])
    setSearchError('')
  }, [])

  const selectFamily = React.useCallback((person) => {
    if (!person?.family) return
    setRecentProfiles((current) => [
      person,
      ...current.filter((recent) => recent.id !== person.id),
    ].slice(0, 5))
    setSelectedTeacher(null)
    setIsTasksOpen(false)
    setIsTeacherDirectoryOpen(false)
    setIsAiOpen(false)
    setSelectedFamily(person.family)
    setSearchQuery('')
    setSearchResults([])
    setSearchError('')
  }, [])

  const selectProfile = React.useCallback((person) => {
    if (person?.type === 'Teacher') selectTeacher(person)
    if (person?.type === 'Student') selectFamily(person)
  }, [selectFamily, selectTeacher])

  const leaveProfile = React.useCallback(() => {
    setSelectedTeacher(null)
    setSelectedFamily(null)
    setSearchQuery('')
    setSearchResults([])
    setSearchError('')
  }, [])

  const openTasks = React.useCallback(() => {
    setSelectedTeacher(null)
    setSelectedFamily(null)
    setIsTeacherDirectoryOpen(false)
    setIsAiOpen(false)
    setIsTasksOpen(true)
  }, [])

  const leaveTasks = React.useCallback(() => {
    setIsTasksOpen(false)
  }, [])

  const openTeacherDirectory = React.useCallback(() => {
    setSelectedTeacher(null)
    setSelectedFamily(null)
    setIsTasksOpen(false)
    setIsAiOpen(false)
    setIsTeacherDirectoryOpen(true)
  }, [])

  const leaveTeacherDirectory = React.useCallback(() => {
    setIsTeacherDirectoryOpen(false)
  }, [])

  const selectDirectoryTeacher = React.useCallback((teacher) => {
    const id = teacherIdFrom(teacher) || normalizeText(teacher?._id) || normalizeText(teacher?.Email)
    selectTeacher({
      id: `teacher-${id}`,
      name: normalizeText(teacher?.Name || teacher?.name),
      detail: getTeacherInstruments(teacher).join(', '),
      image: getTeacherImageUrl(teacher),
      type: 'Teacher',
      teacher,
    })
  }, [selectTeacher])

  const openTask = React.useCallback((task) => {
    if (typeof onOpenTask !== 'function') return
    setIsOpen(false)
    onOpenTask(task)
  }, [onOpenTask])

  const submitAiQuestion = React.useCallback(async (event) => {
    event.preventDefault()
    const question = normalizeText(aiDraft)
    if (!question || isAiLoading) return

    const nextId = () => {
      aiMessageSequence.current += 1
      return aiMessageSequence.current
    }
    const history = aiMessages
      .filter((message) => message.role === 'question' || message.role === 'answer')
      .map((message) => ({
        role: message.role === 'question' ? 'user' : 'assistant',
        content: message.text,
      }))

    setAiMessages((currentMessages) => [
      ...currentMessages,
      { id: `question-${nextId()}`, role: 'question', text: question },
    ])
    setAiDraft('')
    setIsTasksOpen(false)
    setIsTeacherDirectoryOpen(false)
    setIsAiOpen(true)
    setIsAiLoading(true)

    try {
      const result = await askAtharoAi({
        question,
        messages: history,
        conversationId: aiConversationId,
      })
      setAiMessages((currentMessages) => [
        ...currentMessages,
        {
          id: `answer-${nextId()}`,
          role: 'answer',
          text: result.answer,
          sources: result.sources,
        },
      ])
      if (result.conversationId) setAiConversationId(result.conversationId)
    } catch (error) {
      setAiMessages((currentMessages) => [
        ...currentMessages,
        {
          id: `answer-${nextId()}`,
          role: 'error',
          text: error?.response?.status === 503
            ? 'Atharo AI is still being set up. Please try again later.'
            : 'I could not reach Atharo AI. Please try again.',
        },
      ])
    } finally {
      setIsAiLoading(false)
    }
  }, [aiConversationId, aiDraft, aiMessages, isAiLoading])

  const leaveAi = React.useCallback(() => {
    setIsAiOpen(false)
  }, [])

  const refreshTeacherProfile = React.useCallback((updatedTeacher) => {
    if (!updatedTeacher || typeof updatedTeacher !== 'object') return
    const updatedTeacherId = teacherIdFrom(updatedTeacher)

    setSelectedTeacher((currentTeacher) => {
      if (!currentTeacher) return currentTeacher
      const currentTeacherId = teacherIdFrom(currentTeacher)
      if (updatedTeacherId && currentTeacherId && updatedTeacherId !== currentTeacherId) {
        return currentTeacher
      }
      return { ...currentTeacher, ...updatedTeacher }
    })

    setRecentProfiles((currentProfiles) => currentProfiles.map((person) => {
      if (person.type !== 'Teacher') return person
      const currentTeacherId = teacherIdFrom(person.teacher)
      if (updatedTeacherId && currentTeacherId && updatedTeacherId !== currentTeacherId) {
        return person
      }
      const teacher = { ...person.teacher, ...updatedTeacher }
      return {
        ...person,
        name: normalizeText(teacher.Name || teacher.name) || person.name,
        detail: getTeacherInstruments(teacher).join(', '),
        image: getTeacherImageUrl(teacher),
        teacher,
      }
    }))
  }, [])

  React.useEffect(() => {
    if (!selectedTeacher) return undefined
    const identifier = normalizeText(
      selectedTeacher.Email
      || selectedTeacher.email
      || teacherIdFrom(selectedTeacher)
    )
    const requestKey = normalizeForComparison(identifier)
    const requestCache = teacherProfileRequests.current
    if (!identifier || requestCache.has(requestKey)) return undefined

    let active = true
    let settled = false
    requestCache.add(requestKey)
    axios.get(`${baseUrl}/data/${encodeURIComponent(identifier)}`, getRequestHeaders())
      .then((response) => {
        settled = true
        if (!active) return
        const loadedTeacher = response?.data?.teacher || response?.data
        if (!loadedTeacher || typeof loadedTeacher !== 'object' || Array.isArray(loadedTeacher)) return
        refreshTeacherProfile({
          ...selectedTeacher,
          ...loadedTeacher,
          Address: {
            ...(selectedTeacher.Address && typeof selectedTeacher.Address === 'object'
              ? selectedTeacher.Address
              : {}),
            ...(loadedTeacher.Address && typeof loadedTeacher.Address === 'object'
              ? loadedTeacher.Address
              : {}),
          },
        })
      })
      .catch((error) => {
        settled = true
        requestCache.delete(requestKey)
        console.error('Unable to enrich the Hub teacher profile:', error)
      })

    return () => {
      active = false
      if (!settled) requestCache.delete(requestKey)
    }
  }, [refreshTeacherProfile, selectedTeacher])

  const refreshFamilyProfile = React.useCallback((updatedFamily) => {
    if (!updatedFamily || typeof updatedFamily !== 'object') return
    const updatedFamilyId = familyIdFrom(updatedFamily)

    setSelectedFamily((currentFamily) => {
      if (!currentFamily) return currentFamily
      const currentFamilyId = familyIdFrom(currentFamily)
      if (updatedFamilyId && currentFamilyId && updatedFamilyId !== currentFamilyId) {
        return currentFamily
      }
      return { ...currentFamily, ...updatedFamily }
    })

    setRecentProfiles((currentProfiles) => currentProfiles.map((person) => {
      if (person.type !== 'Student') return person
      const currentFamilyId = familyIdFrom(person.family)
      if (updatedFamilyId && currentFamilyId && updatedFamilyId !== currentFamilyId) {
        return person
      }

      const family = { ...person.family, ...updatedFamily }
      const students = Array.isArray(family.students) ? family.students : []
      const studentNames = students
        .map((student) => normalizeText(student?.studentName || student?.name))
        .filter(Boolean)
      const parentName = getParentName(family)
      const name = normalizeText(family.familyName || family.name) || person.name
      return {
        ...person,
        name,
        detail: `${studentNames.join(', ')}${parentName && parentName !== name ? ` (${parentName})` : ''}`,
        family,
      }
    }))
  }, [])

  const viewFullTeacherProfile = React.useCallback(() => {
    if (!selectedTeacher || typeof onViewFullTeacherProfile !== 'function') return
    setIsOpen(false)
    onViewFullTeacherProfile(selectedTeacher, refreshTeacherProfile)
  }, [onViewFullTeacherProfile, refreshTeacherProfile, selectedTeacher])

  const viewFullStudentProfile = React.useCallback(() => {
    if (!selectedFamily || typeof onViewFullStudentProfile !== 'function') return
    setIsOpen(false)
    onViewFullStudentProfile(selectedFamily, refreshFamilyProfile)
  }, [onViewFullStudentProfile, refreshFamilyProfile, selectedFamily])

  const selectedProfileType = selectedTeacher
    ? 'Teacher'
    : selectedFamily
      ? 'Student'
      : ''
  const isHubDetailView = Boolean(selectedProfileType || isAiOpen || isTasksOpen || isTeacherDirectoryOpen)
  const hubTitle = isTeacherDirectoryOpen
    ? 'Teacher Database'
    : isTasksOpen
    ? "Today's Tasks"
    : isAiOpen
    ? 'Ask Atharo AI'
    : selectedProfileType
      ? `${selectedProfileType} Profile`
      : 'Atharo Hub'

  return (
    <aside className={`atharo-hub ${isOpen ? 'atharo-hub--open' : ''}`} aria-label='Atharo Hub'>
      <button
        ref={toggleButtonRef}
        type='button'
        className='atharo-hub__toggle'
        aria-controls='atharo-hub-panel'
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Close Atharo Hub' : 'Open Atharo Hub'}
        onClick={() => setIsOpen((current) => !current)}
      >
        {isOpen ? <BsChevronRight aria-hidden='true' /> : <BsChevronLeft aria-hidden='true' />}
      </button>

      <section
        id='atharo-hub-panel'
        className='atharo-hub__panel'
        aria-hidden={!isOpen}
      >
        <header className={`atharo-hub__header ${isHubDetailView ? 'atharo-hub__header--profile' : ''}`}>
          {isHubDetailView && (
            <button
              type='button'
              aria-label='Back to Atharo Hub'
              onClick={isTeacherDirectoryOpen
                ? leaveTeacherDirectory
                : isTasksOpen ? leaveTasks : isAiOpen ? leaveAi : leaveProfile}
            >
              <BsChevronLeft aria-hidden='true' />
            </button>
          )}
          <h2>
            {isTasksOpen && <MdTaskAlt aria-hidden='true' />}
            {isTeacherDirectoryOpen && <MdSchool aria-hidden='true' />}
            {isAiOpen && <MdAutoAwesome aria-hidden='true' />} {hubTitle}
          </h2>
          <button type='button' aria-label='Close Atharo Hub' onClick={closeHub}>
            <BsX aria-hidden='true' />
          </button>
        </header>

        {isTeacherDirectoryOpen ? (
          <TeacherDirectory userData={userData} onSelectTeacher={selectDirectoryTeacher} />
        ) : isTasksOpen ? (
          <TodayTasks
            tasks={tasksDueToday}
            loading={tasksLoading}
            error={tasksError}
            onRetry={loadTasks}
            onTaskClick={openTask}
          />
        ) : isAiOpen ? (
          <AtharoAi
            messages={aiMessages}
            draft={aiDraft}
            setDraft={setAiDraft}
            onSubmit={submitAiQuestion}
            isLoading={isAiLoading}
          />
        ) : selectedTeacher ? (
          <TeacherProfile
            key={normalizeText(selectedTeacher.TeacherID || selectedTeacher.teacherId || selectedTeacher.id)}
            teacher={selectedTeacher}
            activeTab={selectedTeacherTab}
            setActiveTab={setSelectedTeacherTab}
            onViewFullProfile={viewFullTeacherProfile}
          />
        ) : selectedFamily ? (
          <StudentProfile
            key={familyIdFrom(selectedFamily)}
            family={selectedFamily}
            onFamilyLoaded={refreshFamilyProfile}
            onViewFullProfile={viewFullStudentProfile}
          />
        ) : <div className='atharo-hub__body'>
          <label className='atharo-hub__search'>
            <BsSearch aria-hidden='true' />
            <span className='sr-only'>Search Atharo Hub</span>
            <input
              type='search'
              placeholder='Search by name…'
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              autoComplete='off'
            />
          </label>

          {hasSearchQuery ? (
            <section className='atharo-hub__search-results' aria-label='People search results' aria-live='polite'>
              {isSearching && <div className='atharo-hub__search-message'>Searching…</div>}
              {!isSearching && searchError && (
                <div className='atharo-hub__search-message atharo-hub__search-message--error' role='alert'>
                  {searchError}
                </div>
              )}
              {!isSearching && !searchError && searchResults.length === 0 && (
                <div className='atharo-hub__search-message'>No matching teachers or students found.</div>
              )}
              {!isSearching && searchResults.length > 0 && (
                <div className='atharo-hub__people-list'>
                  {searchResults.map((person) => (
                    <button
                      type='button'
                      className='atharo-hub__person-row'
                      key={person.id}
                      onClick={() => selectProfile(person)}
                    >
                      <PersonAvatar person={person} />
                      <span className='atharo-hub__person-copy'>
                        <span className='atharo-hub__person-name'>{person.name}</span>
                        {person.detail && <span className='atharo-hub__person-detail'>{person.detail}</span>}
                      </span>
                      <span className={`atharo-hub__type ${person.searchTypeLabel === 'Old Teacher' ? 'atharo-hub__type--old-teacher' : ''}`}>
                        {person.searchTypeLabel || person.type}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          ) : (
            <section className='atharo-hub__section' aria-labelledby='atharo-hub-recent-heading'>
              <h3 id='atharo-hub-recent-heading'>Recent</h3>
              <div className='atharo-hub__recent-list'>
                {recentProfiles.map((item) => (
                  <button type='button' className='atharo-hub__recent-row' key={item.id} onClick={() => selectProfile(item)}>
                    <PersonAvatar person={item} />
                    <span className='atharo-hub__recent-name'>{item.name}</span>
                    <span className='atharo-hub__type'>{item.type}</span>
                  </button>
                ))}
                {recentProfiles.length === 0 && (
                  <div className='atharo-hub__empty-recent'>Profiles you open will appear here.</div>
                )}
              </div>
            </section>
          )}

          <section
            className='atharo-hub__section'
            aria-labelledby={hasSearchQuery ? undefined : 'atharo-hub-pages-heading'}
            aria-label={hasSearchQuery ? 'Related pages' : undefined}
          >
            {!hasSearchQuery && <h3 id='atharo-hub-pages-heading'>Pages</h3>}
            <div className='atharo-hub__page-list'>
              {visiblePageButtons.map(({ label, icon: PageIcon }) => (
                <button
                  type='button'
                  className='atharo-hub__page-button'
                  key={label}
                  onClick={label === 'Tasks'
                    ? openTasks
                    : label === 'Teacher Database' ? openTeacherDirectory : undefined}
                  aria-label={label === 'Tasks'
                    ? `Tasks, ${tasksDueToday.length} due today`
                    : undefined}
                >
                  <span className='atharo-hub__page-icon' aria-hidden='true'><PageIcon /></span>
                  <span className='atharo-hub__page-label'>{label}</span>
                  {label === 'Tasks' && tasksDueToday.length > 0 && (
                    <span className='atharo-hub__page-notification' aria-hidden='true'>
                      {tasksDueToday.length > 9 ? '9+' : tasksDueToday.length}
                    </span>
                  )}
                  <BsChevronRight className='atharo-hub__page-chevron' aria-hidden='true' />
                </button>
              ))}
            </div>
          </section>

          <section className='atharo-hub__ai' aria-labelledby='atharo-hub-ai-heading'>
            <h3 id='atharo-hub-ai-heading'><MdAutoAwesome aria-hidden='true' /> Ask Atharo AI</h3>
            <p>Get instant answers from our Confluence wiki</p>
            <form className='atharo-hub__ai-input' onSubmit={submitAiQuestion}>
              <input
                type='text'
                placeholder='Ask a question…'
                aria-label='Ask Atharo AI'
                value={aiDraft}
                onChange={(event) => setAiDraft(event.target.value)}
                autoComplete='off'
                disabled={isAiLoading}
              />
              <button type='submit' aria-label='Submit Atharo AI question' disabled={isAiLoading || !aiDraft.trim()}>
                <BsChevronRight aria-hidden='true' />
              </button>
            </form>
          </section>
        </div>}
      </section>
    </aside>
  )
}

export default AtharoHub
